/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Chatbot Widget Studio Main Dashboard View (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useWidgetStudioStore } from '../stores/useWidgetStudioStore';
import { useI18n } from '../hooks/useI18n';
import { WidgetAppearanceTab } from '../components/widget/WidgetAppearanceTab';
import { WidgetBehaviorTab } from '../components/widget/WidgetBehaviorTab';
import { WidgetLeadCaptureTab } from '../components/widget/WidgetLeadCaptureTab';
import { WidgetSecurityTab } from '../components/widget/WidgetSecurityTab';
import { WidgetEmbedCodeTab } from '../components/widget/WidgetEmbedCodeTab';
import { WidgetLivePreview } from '../components/widget/WidgetLivePreview';
import {
  Palette,
  MessageSquare,
  UserCheck,
  Shield,
  Code2,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';

export const WidgetStudioView: React.FC = () => {
  const { t } = useI18n();
  const {
    activeTab,
    hasUnsavedChanges,
    isSaving,
    setActiveTab,
    saveConfig,
    resetConfigToDefault,
  } = useWidgetStudioStore();

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleSave = async () => {
    await saveConfig();
    setToastMessage(t('widget.saveSuccessToast'));
    setTimeout(() => setToastMessage(null), 3000);
  };

  const tabs = [
    { id: 'appearance', label: t('widget.tabAppearance'), icon: Palette },
    { id: 'behavior', label: t('widget.tabBehavior'), icon: MessageSquare },
    { id: 'leads', label: t('widget.tabLeads'), icon: UserCheck },
    { id: 'security', label: t('widget.tabSecurity'), icon: Shield },
    { id: 'embed', label: t('widget.tabEmbed'), icon: Code2 },
  ] as const;

  return (
    <div className="space-y-6">
      {/* 1. Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-surface-border bg-surface shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-heading font-extrabold text-foreground">
              {t('widget.title')}
            </h2>
            <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-accent/15 text-accent border border-accent/20">
              <Sparkles className="w-3 h-3" />
              <span>{t('widget.zeroCssLeakage')}</span>
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('widget.studioSubtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {hasUnsavedChanges ? (
            <span className="flex items-center gap-1.5 text-xs text-amber-500 font-medium px-2.5 py-1 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>{t('widget.hasUnsavedChanges')}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-emerald-500 font-medium px-2.5 py-1 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{t('widget.syncedChanges')}</span>
            </span>
          )}

          <button
            type="button"
            onClick={resetConfigToDefault}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted hover:text-foreground transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t('widget.resetDefaultBtn')}</span>
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50 transition-opacity shadow-sm"
          >
            <Save className="w-3.5 h-3.5" />
            <span>{isSaving ? t('widget.savingBtn') : t('widget.saveConfigBtn')}</span>
          </button>
        </div>
      </div>

      {/* Toast Feedback */}
      {toastMessage && (
        <div className="p-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 2. Main 2-Column Studio Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Configuration Tabs (7 cols) */}
        <div className="lg:col-span-7 rounded-2xl border border-surface-border bg-surface p-5 sm:p-6 shadow-sm space-y-6">
          {/* Navigation Tabs Bar */}
          <div className="flex border-b border-surface-border gap-1 overflow-x-auto pb-px">
            {tabs.map((tab) => {
              const isSelected = activeTab === tab.id;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    isSelected
                      ? 'border-primary text-primary'
                      : 'border-transparent text-foreground-muted hover:text-foreground hover:border-surface-border'
                  }`}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active Tab Panel */}
          <div>
            {activeTab === 'appearance' && <WidgetAppearanceTab />}
            {activeTab === 'behavior' && <WidgetBehaviorTab />}
            {activeTab === 'leads' && <WidgetLeadCaptureTab />}
            {activeTab === 'security' && <WidgetSecurityTab />}
            {activeTab === 'embed' && <WidgetEmbedCodeTab />}
          </div>
        </div>

        {/* Right Column: Live Interactive Mockup Preview (5 cols) */}
        <div className="lg:col-span-5 rounded-2xl border border-surface-border bg-surface p-5 sm:p-6 shadow-sm space-y-4">
          <div className="space-y-0.5">
            <h3 className="font-heading font-bold text-sm text-foreground">
              {t('widget.livePreviewTitle')}
            </h3>
            <p className="text-[11px] text-foreground-muted">
              {t('widget.livePreviewSubtitle')}
            </p>
          </div>

          <WidgetLivePreview />
        </div>
      </div>
    </div>
  );
};
